import {isPlanetHdProduct, normalizeRemoteSensingSource} from './remoteSensingModel'

export const REMOTE_SENSING_DATE_STATES = {
  CANDIDATE: 'candidate',
  PREPARING: 'preparing',
  READY: 'ready',
  REJECTED_QUALITY: 'rejected_quality',
  REJECTED: 'rejected',
  FAILED: 'failed',
}

const STATUS_ALIASES = {
  candidate: REMOTE_SENSING_DATE_STATES.CANDIDATE,
  available: REMOTE_SENSING_DATE_STATES.CANDIDATE,
  discovered: REMOTE_SENSING_DATE_STATES.CANDIDATE,
  queued: REMOTE_SENSING_DATE_STATES.PREPARING,
  requested: REMOTE_SENSING_DATE_STATES.PREPARING,
  preparing: REMOTE_SENSING_DATE_STATES.PREPARING,
  processing: REMOTE_SENSING_DATE_STATES.PREPARING,
  delivered: REMOTE_SENSING_DATE_STATES.READY,
  succeeded: REMOTE_SENSING_DATE_STATES.READY,
  ready: REMOTE_SENSING_DATE_STATES.READY,
  rejected: REMOTE_SENSING_DATE_STATES.REJECTED,
  rejected_quality: REMOTE_SENSING_DATE_STATES.REJECTED_QUALITY,
  unusable: REMOTE_SENSING_DATE_STATES.REJECTED,
  failed: REMOTE_SENSING_DATE_STATES.FAILED,
  error: REMOTE_SENSING_DATE_STATES.FAILED,
}

const getDateValue = (value) => {
  if (typeof value === 'string') {
    return value.slice(0, 10)
  }

  return String(
    value?.date || value?.acquired_at || value?.datetime || value?.item_datetime || value?.value || ''
  ).slice(0, 10)
}

const optionalNumber = (value) => {
  if (value == null || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const normalizeRemoteSensingDateItem = (value, source) => {
  const normalizedSource = normalizeRemoteSensingSource(source)
  const isHd = isPlanetHdProduct(normalizedSource.product)
  const rawStatus = typeof value === 'object' && value
    ? value.status || value.state || value.delivery_status
    : null
  const status = STATUS_ALIASES[String(rawStatus || '').toLowerCase()] ||
    (isHd ? REMOTE_SENSING_DATE_STATES.CANDIDATE : REMOTE_SENSING_DATE_STATES.READY)

  return {
    date: getDateValue(value),
    status,
    acquisitionId: value?.acquisition_id || value?.acquisitionId || null,
    accessId: value?.access_id || value?.accessId || null,
    itemId: value?.item_id || value?.itemId || value?.scene_id || null,
    resultId: value?.result_id || value?.resultId || null,
    algorithmVersion: value?.algorithm_version || value?.algorithmVersion || null,
    geometryHash: value?.geometry_hash || value?.geometryHash || null,
    resolutionMeters: optionalNumber(
      value?.resolution_m ?? value?.resolution_meters ?? value?.resolution
    ),
    qualityWarning: value?.quality_warning || value?.qualityWarning || null,
    cloudPercent: optionalNumber(
      value?.cloud_percent ??
      (value?.cloud_fraction == null ? null : Number(value.cloud_fraction) * 100) ??
      value?.cloud_coverage ??
      value?.cloudCoverage
    ),
    coveragePercent: optionalNumber(
      value?.coverage_percent ??
      (value?.coverage_fraction == null ? null : Number(value.coverage_fraction) * 100) ??
      (value?.quality?.coverage_fraction == null ? null : Number(value.quality.coverage_fraction) * 100) ??
      value?.coveragePercent
    ),
    validPercent: optionalNumber(
      value?.valid_percent ??
      (value?.valid_fraction == null ? null : Number(value.valid_fraction) * 100) ??
      (value?.quality?.valid_fraction == null ? null : Number(value.quality.valid_fraction) * 100) ??
      value?.validPercent
    ),
    clearPercent: optionalNumber(
      value?.clear_percent ??
      (value?.clear_fraction == null ? null : Number(value.clear_fraction) * 100) ??
      (value?.quality?.clear_fraction == null ? null : Number(value.quality.clear_fraction) * 100) ??
      value?.clearPercent
    ),
    assets: value?.assets || null,
    reason: value?.reason || value?.rejection_reason || value?.error || null,
    source: normalizedSource,
    raw: typeof value === 'object' && value ? value : null,
  }
}

export const normalizeRemoteSensingDateItems = (values, source) => {
  const items = Array.isArray(values) ? values : []
  const byIdentity = new Map()

  items.forEach((value) => {
    const item = normalizeRemoteSensingDateItem(value, source)
    if (!item.date) {
      return
    }

    const identity = `${item.date}:${item.acquisitionId || item.itemId || item.resultId || 'date'}`
    byIdentity.set(identity, item)
  })

  return Array.from(byIdentity.values()).sort((left, right) =>
    right.date.localeCompare(left.date)
  )
}

export const isRemoteSensingDateReady = (item) =>
  item?.status === REMOTE_SENSING_DATE_STATES.READY

export const isRemoteSensingDateVisualizable = (item) => [
  REMOTE_SENSING_DATE_STATES.READY,
  REMOTE_SENSING_DATE_STATES.REJECTED_QUALITY,
].includes(item?.status)

export const canPrepareRemoteSensingDate = (item) =>
  item?.status === REMOTE_SENSING_DATE_STATES.CANDIDATE
